import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { addPaymentMethod } from '../../../utils/stripe/api';

interface AddPaymentMethodModalProps {
  userId: string;
  onClose: () => void;
  onAdd: (paymentMethod: any) => void;
}

export function AddPaymentMethodModal({ userId, onClose, onAdd }: AddPaymentMethodModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isStripeReady, setIsStripeReady] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  // Check if Stripe.js is loaded
  useEffect(() => {
    if (!stripe || !elements) {
      console.log('Stripe.js is still loading in AddPaymentMethodModal');
      setIsStripeReady(false);
    } else {
      console.log('Stripe.js loaded successfully in AddPaymentMethodModal');
      setIsStripeReady(true);
    }
  }, [stripe, elements]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    } else if (!isStripeReady) {
      setError('Stripe.jsがまだ読み込まれていません。しばらく待ってから再度お試しください。');
      return;
    }
    
    setError(null);
    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('カード情報が入力されていません。');
      }
      
      // Create payment method with Stripe.js
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: '', // We don't need to send the email here
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'カードの登録に失敗しました。');
      }
      
      if (paymentMethod) {
        // Attach the payment method to the customer
        const result = await addPaymentMethod(userId, paymentMethod.id, false);
        
        setIsComplete(true);
        
        // Close modal after showing success message
        setTimeout(() => {
          onAdd(result);
        }, 1500);
      } else {
        throw new Error('カードの登録に失敗しました。');
      }
    } catch (error) {
      console.error('Payment method error:', error);
      setError(error instanceof Error ? error.message : 'カードの登録に失敗しました。');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">お支払い方法を追加</h3>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isComplete ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              カードが登録されました
            </h3>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                カード情報
                {!isStripeReady && <span className="ml-2 text-xs text-blue-600">(読み込み中...)</span>}
              </label>
              {!isStripeReady ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3">
                  <p className="text-yellow-700 text-sm">
                    Stripe.jsの読み込み中です。しばらくお待ちください...
                  </p>
                </div>
              ) : null}
              <div className="p-3 border border-gray-300 rounded-md bg-white">
                <CardElement 
                  options={{
                    disabled: !isStripeReady,
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
              </div>
              <p className="text-xs text-gray-500">
                テスト用カード番号: 4242 4242 4242 4242 (有効期限は未来の日付、CVCは任意の3桁)
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={!isStripeReady || isProcessing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <CreditCard className="w-4 h-4 animate-pulse" />
                  処理中...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  カードを登録する
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}